import { useEffect, useState } from "react";
import axios from "axios";
import { api } from "../../lib/api";
import { MessageSquare, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => { try { return new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

// Hilo de comentarios del presupuesto (cliente ↔ empresa). Reutilizable.
// path: ruta API después de /api (ej. "/crm/public/quote/TOKEN/comments" o "/crm/leads/ID/quote/comments")
// role: "client" (público, pide nombre) | "company" (interno)
export default function QuoteComments({ path, role = "company", accent = "#7c5cff", isPublic = false }) {
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => (isPublic ? axios.get(`${API}${path}`) : api.get(path)).then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [path]);

  const submit = async () => {
    setErr("");
    if (!text.trim()) return;
    if (role === "client" && !name.trim()) { setErr("Escribe tu nombre para comentar."); return; }
    setBusy(true);
    try {
      const body = { text: text.trim(), name: name.trim() };
      if (isPublic) await axios.post(`${API}${path}`, body); else await api.post(path, body);
      setText(""); await load();
    } catch (e) { setErr("No se pudo enviar el comentario."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="quote-comments">
      <div className="text-sm font-medium text-navy flex items-center gap-1.5 mb-3">
        <MessageSquare className="w-4 h-4" style={{ color: accent }} /> Comentarios y ajustes
      </div>
      <div className="space-y-3 mb-4 max-h-72 overflow-y-auto thin-scroll pr-1">
        {items.length === 0 && <div className="text-sm text-navy/40">Aún no hay comentarios. Escribe si necesitas algún ajuste.</div>}
        {items.map((c) => {
          const mine = (role === "client" && c.author === "client") || (role === "company" && c.author === "company");
          return (
            <div key={c.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`comment-${c.id}`}>
              <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${c.author === "company" ? "bg-navy text-white" : "border border-navy/10 bg-white text-navy"}`}
                style={c.author === "client" ? { borderColor: `${accent}55` } : undefined}>
                <div className="text-[11px] opacity-70 mb-0.5">{c.author === "company" ? "🏢 " : "👤 "}{c.name} · {when(c.at)}</div>
                <div className="whitespace-pre-line">{c.text}</div>
              </div>
            </div>
          );
        })}
      </div>
      {role === "client" && (
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre"
          className="w-full mb-2 bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy/30" data-testid="comment-name" />
      )}
      <div className="flex items-end gap-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2}
          placeholder={role === "client" ? "Ej. Cambiar el sofá por el modelo azul…" : "Responder al cliente…"}
          className="flex-1 bg-white border border-navy/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy/30 resize-none" data-testid="comment-input" />
        <button onClick={submit} disabled={busy} data-testid="comment-send"
          className="rounded-full text-white px-4 py-2.5 text-sm hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5 shrink-0"
          style={{ backgroundColor: accent }}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar
        </button>
      </div>
      {err && <div className="text-xs text-destructive mt-1" data-testid="comment-error">{err}</div>}
    </div>
  );
}
